import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Avatar, 
  Typography, 
  TextField, 
  Button, 
  Link, 
  Paper, 
  Alert, 
  LinearProgress, 
  InputAdornment, 
  IconButton,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { 
  LockOutlined, 
  Visibility, 
  VisibilityOff,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ProgressIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  MailOutline as MailIcon,
  Search as SearchIcon,
  DirectionsCar as CarIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [googleToken, setGoogleToken] = useState('');
  const [selectedRole, setSelectedRole] = useState('Student');

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse.credential) {
      setError('No credential received from Google.');
      return;
    }

    const idToken = credentialResponse.credential;
    try {
      setError('');
      setLoading(true);
      
      const res = await googleLogin(idToken);
      if (res && res.registrationRequired) {
        setGoogleToken(idToken);
        setOpenRoleDialog(true);
      }
    } catch (err: any) {
      console.error('Google login failed:', err);
      let errMsg = 'Google login failed. Please try again.';
      if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelectionSubmit = async () => {
    try {
      setError('');
      setLoading(true);
      setOpenRoleDialog(false);
      
      await googleLogin(googleToken, selectedRole);
    } catch (err: any) {
      console.error('Google registration failed:', err);
      let errMsg = 'Google registration failed. Please try again.';
      if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
      setGoogleToken('');
    }
  };

  useEffect(() => {
    document.title = "Sign In to Your Account - Adilesson";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Access your driving school dashboard. Book lessons, track DVSA driving syllabus progress, or manage your instructor calendar.');
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('confirmed') === 'true') {
      setSuccessMessage('Your email has been successfully confirmed. You can now sign in.');
    } else if (query.get('confirmed') === 'false') {
      const err = query.get('error');
      if (err === 'invalid_parameters') {
        setError('Invalid verification link.');
      } else {
        setError('Email confirmation failed. The verification link may have expired or is invalid.');
      }
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      // Navigation after login is handled in the AuthContext
    } catch (err: any) {
      console.error('Login failed:', err);
      
      let errorMessage = 'Failed to log in. Please check your credentials and try again.';
      
      if (err.response?.data?.errors?.[0]) {
        errorMessage = err.response.data.errors[0];
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f4f6f9' }}>
      <Grid container sx={{ flexGrow: 1 }}>
        {/* Left pane: Advertising & Features Showcase (Hidden on mobile) */}
        <Grid 
          size={{ md: 6, lg: 7 }} 
          sx={{
            background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
            color: '#fff',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 6,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Logo & Header */}
          <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            <CarIcon sx={{ color: '#00FF84', fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ffffff', letterSpacing: '-0.5px', fontFamily: '"Outfit", sans-serif' }}>
              adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
            </Typography>
          </Box>

          {/* Main feature content */}
          <Box sx={{ my: 'auto', maxWidth: 600 }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.2 }}>
              Drive Your Driving School Forward
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, mb: 5, fontSize: '1.1rem' }}>
              The all-in-one ADI scheduling, DVSA syllabus tracker, and automated student engagement platform.
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              {[
                {
                  icon: <CalendarIcon sx={{ color: '#64b5f6' }} />,
                  title: "Smart Calendar & Booking",
                  desc: "Publish your availability. Students browse and book lesson slots instantly with automatic sync."
                },
                {
                  icon: <ProgressIcon sx={{ color: '#81c784' }} />,
                  title: "DVSA Syllabus Progress Tracker",
                  desc: "Visually mark and rate DVSA syllabus topics so students know exactly when they are test-ready."
                },
                {
                  icon: <WhatsAppIcon sx={{ color: '#a5d6a7' }} />,
                  title: "Automated WhatsApp Alerts",
                  desc: "Send instant booking confirmations, approvals, and reminders directly from your number."
                },
                {
                  icon: <ReceiptIcon sx={{ color: '#ffb74d' }} />,
                  title: "Tax & Expenses Logger",
                  desc: "Track business expenses like fuel, franchise fees, and repairs with receipt uploads."
                },
                {
                  icon: <SchoolIcon sx={{ color: '#90caf9' }} />,
                  title: "Theory Mock Exams & Hazard Clips",
                  desc: "Full mock exams with OGL theory questions and interactive hazard clip practice built-in."
                }
              ].map((feature, idx) => (
                <Box key={idx} display="flex" gap={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                    {feature.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {feature.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Footer Promotion */}
          <Box 
            sx={{ 
              p: 2.5, 
              borderRadius: 4, 
              bgcolor: 'rgba(255, 255, 255, 0.08)', 
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Start your 30-day free trial today
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                No credit card required. Cancel anytime.
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={() => navigate('/register')}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            >
              Sign Up Now
            </Button>
          </Box>
        </Grid>

        {/* Right pane: Login Form */}
        <Grid 
          size={{ xs: 12, md: 6, lg: 5 }} 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
          sx={{ py: 6, px: { xs: 3, sm: 6 } }}
        >
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            {/* Logo on Mobile */}
            <Box display={{ xs: 'flex', md: 'none' }} alignItems="center" justifyContent="center" mb={4} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1a237e', fontSize: 32, mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e', letterSpacing: '-0.5px', fontFamily: '"Outfit", sans-serif' }}>
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>

            <Typography variant="h4" fontWeight="bold" color="#1a237e" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Sign in to manage your lessons, syllabus, and scheduler dashboard.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {successMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {loading && <LinearProgress sx={{ my: 2, borderRadius: 1 }} />}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 1.5, py: 1.5, fontWeight: 'bold', borderRadius: 2.5, textTransform: 'none', fontSize: '1rem' }}
                disabled={loading}
              >
                Sign In
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  OR
                </Typography>
              </Divider>

              <Box display="flex" justifyContent="center" sx={{ mb: 2, width: '100%' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in failed. Please try again.')}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  width="360"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Button
                component={RouterLink}
                to="/search"
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<SearchIcon />}
                sx={{ mb: 3, py: 1.5, fontWeight: 'bold', borderRadius: 2.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Search Instructors
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ fontWeight: 'bold', textDecoration: 'none' }}>
                  Forgot Password?
                </Link>
                <Link component={RouterLink} to="/register" variant="body2" sx={{ fontWeight: 'bold', textDecoration: 'none' }}>
                  {"Don't have an account? Sign Up"}
                </Link>
              </Box>

              <Divider sx={{ my: 4 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Support
                </Typography>
              </Divider>

              <Box 
                sx={{ 
                  p: 2, 
                  borderRadius: 3, 
                  bgcolor: '#f8f9fa', 
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', color: '#1976d2', width: 40, height: 40 }}>
                  <MailIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: '500' }}>
                    Need help or have questions?
                  </Typography>
                  <Box display="flex" gap={1.5} alignItems="center">
                    <Link 
                      component={RouterLink}
                      to="/contact" 
                      sx={{ 
                        fontWeight: 'bold', 
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontSize: '0.875rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Contact Form
                    </Link>
                    <Typography variant="caption" color="text.secondary">|</Typography>
                    <Link 
                      href="mailto:support@adilesson.co.uk" 
                      sx={{ 
                        fontWeight: 'bold', 
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontSize: '0.875rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Email Us
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Role Selection Dialog for First-Time Google Users */}
      <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Select Account Type
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            To complete your registration with Google, please select if you are a Student or a Driving Instructor:
          </Typography>
          <RadioGroup
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            <FormControlLabel
              value="Student"
              control={<Radio />}
              label="I am a Student (looking for driving lessons)"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              value="Instructor"
              control={<Radio />}
              label="I am a Driving Instructor (looking to manage students)"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpenRoleDialog(false)} color="inherit" sx={{ fontWeight: 'bold' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRoleSelectionSubmit}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Complete Sign Up
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
